import {
  Box,
  Center,
  Container,
  Flex,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { SetStateAction, useRef, useState } from 'react'

import ChacraLogo from '@/assets/images/logo-green.png'
import FastForwardIcon from '@/assets/images/playerIcons/fastForwardIcon.svg'
import FullScreenIcon from '@/assets/images/playerIcons/fullScreenIcon.svg'
import Image from 'next/image'
import Link from 'next/link'
import PauseIcon from '@/assets/images/playerIcons/pauseIcon.svg'
import PlayIcon from '@/assets/images/playerIcons/playIcon.svg'
import ReactPlayer from 'react-player'
import ReverseIcon from '@/assets/images/playerIcons/reverseIcon.svg'
import { Review_review } from '@/apollo/__generated__/Review'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import SettingsIcon from '@/assets/images/playerIcons/settingsIcon.svg'
import VolumeIcon from '@/assets/images/playerIcons/volumeIcon.svg'
import gsap from 'gsap'
import screenful from 'screenfull'
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect'
import useSelector from '@/hooks/useSelector'

gsap.registerPlugin(ScrollTrigger)

export interface ReviewProps {
  review: Review_review | null
}

const format = (seconds: any) => {
  if (isNaN(seconds)) {
    return `00:00`
  }
  const date = new Date(seconds * 1000)
  const hh = date.getUTCHours()
  const mm = date.getUTCMinutes()
  const ss = date.getUTCSeconds().toString().padStart(2, '0')
  if (hh) {
    return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`
  }
  return `${mm}:${ss}`
}

const HeaderVideo = (props: ReviewProps) => {
  const [q, mainRef] = useSelector<HTMLDivElement>(null)
  const playerRef = useRef<ReactPlayer>(null)
  const videoBoxRef = useRef(null)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const tl = useRef<gsap.core.Timeline>(null!)
  const [mainPlay, setMainPlay] = useState(false)
  const [play, setPlay] = useState(true)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [played, setPlayed] = useState(0)
  const [seeking, setSeeking] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)

  useIsomorphicLayoutEffect(() => {
    tl.current = gsap.timeline({
      onComplete: () => {
        tl.current.to(q('#review-video'), {
          y: 0,
          rotation: 0,
          transformOrigin: 'left top',
          duration: 1,
          delay: 0.5,
        })
      },
    })
    ScrollTrigger.matchMedia({
      // desktop
      '(min-width: 48em)': function () {
        tl.current?.fromTo(
          q('#review-video'),
          { y: -1000, scale: 0.3 },
          { y: -200, scale: 1, duration: 1.5, delay: 0.1 }
        )

        tl.current.to(q('#review-video'), {
          y: 0,
          rotation: -2,
          transformOrigin: 'left top',
          duration: 0.5,
          delay: 0.3,
        })

        tl.current.fromTo(
          q('#heading-section'),
          {
            y: 200,
            opacity: 0,
            delay: 0.1,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
          },
          '<'
        )

        tl.current.fromTo(
          q('#header-chacra'),
          {
            x: -300,
          },
          {
            x: 0,
            duration: 0.5,
          },
          '<'
        )

        tl.current.to(q('#review-video'), {
          rotation: 0,
          duration: 0.5,
          ease: 'power2.out',
          transformOrigin: 'left top',
          scrollTrigger: {
            id: 'parallax',
            trigger: q('#review-video'),
            start: '300px 30%',
            end: '+=900',
            markers: false,
            toggleActions: 'pause play none none',
          },
        })

        return () => {
          tl.current.scrollTrigger?.kill()
          tl.current.kill()
        }
      },
    })
  }, [])

  const currentTime =
    playerRef && playerRef.current
      ? playerRef.current.getCurrentTime()
      : '00:00'

  const duration =
    playerRef && playerRef.current ? playerRef.current.getDuration() : '00:00'

  const elapsedTime = format(currentTime)

  const totalDuration = format(duration)

  const handleRewind = () => {
    playerRef.current?.seekTo(playerRef.current.getCurrentTime() - 5)
  }

  const handleFastForward = () => {
    playerRef.current?.seekTo(playerRef.current.getCurrentTime() + 5)
  }

  const handleVolumeChange = (newValue: number) => {
    console.log(newValue)
    setVolume(parseFloat((newValue / 100).toLocaleString()))
    setIsMuted(newValue === 0 ? true : false)
  }

  const handleProgress = (changeState: { played: SetStateAction<number> }) => {
    if (!seeking) {
      setPlayed(changeState.played)
    }
  }

  const handleSeekChange = (newValue: number) => {
    setPlayed(parseFloat((newValue / 100).toString()))
  }

  const handleSeekMouseDown = () => {
    setSeeking(true)
  }

  const handleSeekMouseUp = (newValue: number) => {
    setSeeking(false)
    playerRef.current?.seekTo(newValue / 100, 'fraction')
  }

  const toggleFullScreen = () => {
    if (videoBoxRef.current && window.document && document !== undefined)
      screenful.toggle(videoBoxRef.current)
  }

  const playVideo = () => {
    setMainPlay(true)
    gsap.to(q('#review-video'), {
      rotation: 0,
      duration: 0.5,
      ease: 'power2.out',
    })
  }

  return (
    <Box ref={mainRef}>
      <Flex
        id="header-chacra"
        position="absolute"
        top={{ base: 20, md: '10' }}
        left="0"
        w={{ base: 'container.xl', md: 'full' }}
        justifyContent={{ base: 'center', md: 'space-between' }}
        alignItems="center"
        px={{ md: '6', lg: '12' }}
        h={{ base: '100px', md: '100px' }}
        zIndex={1}
      >
        <Link href="/">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a>
            <Box
              h="100px"
              w={{ base: '150px', md: '160px', lg: '180px' }}
              position="relative"
            >
              <Image
                src={ChacraLogo}
                layout="responsive"
                objectFit="contain"
                priority
                alt="chacra-logo"
              />
            </Box>
          </a>
        </Link>
      </Flex>
      <Container
        centerContent
        minH="100vh"
        maxW="container.xl"
        w="container.xl"
        pt={{ base: 40, md: 40 }}
      >
        <VStack spacing={{ base: 40, md: 50 }}>
          <Stack
            spacing={{ base: 10, md: 0 }}
            flexDirection={{ base: 'column', md: 'row' }}
            id="heading-section"
            h={{ md: '100px' }}
          >
            <Box
              alignSelf="center"
              id="heading-logo"
              mr={{ md: 10 }}
              w={{ base: '400px', md: '327px' }}
              h={{ base: '200px', md: 'full' }}
              position="relative"
            >
              <Image
                priority
                objectFit="fill"
                layout="fill"
                src={
                  props.review?.logo?.publicUrl
                    ? props.review?.logo.publicUrl
                    : ''
                }
                alt="review-logo"
              ></Image>
            </Box>
            <Box
              alignSelf="center"
              id="heading-divider"
              h={{ base: '0', md: '80px' }}
              w={{ base: '500px', md: '0px' }}
              borderLeft={{ md: '4px' }}
              borderBottom={{ base: '8px', md: 0 }}
              borderColor="#006400"
            ></Box>
            <Box
              w={{ base: '800px', md: 'full' }}
              id="heading-desc"
              alignSelf="center"
              justifyContent="center"
            >
              <Text
                px={{ md: 10 }}
                fontSize="4xl"
                fontWeight="bold"
                color="#006400"
                textAlign={{ base: 'center', md: 'left' }}
              >
                {props.review?.description}
              </Text>
            </Box>
          </Stack>

          <Box
            as="div"
            ref={videoBoxRef}
            id="review-video"
            position="relative"
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="xl"
            bgColor="black"
            h="670px"
            w="1200px"
          >
            <Box
              position="relative"
              overflow="hidden"
              borderRadius="2xl"
              boxShadow="xl"
            >
              <ReactPlayer
                ref={playerRef}
                position="absolute"
                top={0}
                left={0}
                url={props.review?.video}
                width="100%"
                height="100%"
                controls={false}
                playing={play}
                volume={volume}
                muted={isMuted}
                playbackRate={playbackRate}
                onProgress={handleProgress}
                onEnded={() => setPlay(false)}
                playsinline
                playIcon={
                  <Center
                    id="start-video-learn-btn"
                    h="700px"
                    w="1200px"
                    onClick={playVideo}
                  >
                    <Box position="relative" h="150px" w="150px">
                      <Image
                        id="img-start-video-learn-btn"
                        onClick={playVideo}
                        src="https://res.cloudinary.com/hfq3eckim/image/upload/v1651132886/csv2/Icons/Play_Button_aobpih.svg"
                        alt="play-button"
                        layout="fill"
                        priority
                      />
                    </Box>
                  </Center>
                }
                light={
                  props.review?.placeholder?.publicUrl
                    ? props.review?.placeholder?.publicUrl
                    : ''
                }
              />
            </Box>
            <Flex
              display={mainPlay ? 'flex' : 'none'}
              position="absolute"
              left={0}
              right={0}
              bottom={10}
              zIndex={1}
              h="25px"
              w="full"
              justify="space-around"
            >
              <Box
                cursor="pointer"
                onClick={handleRewind}
                position="relative"
                w="25px"
                h="25px"
              >
                <Image layout="fill" src={ReverseIcon} alt="reverse-icon" />
              </Box>

              {play ? (
                <Box
                  id="pause-learn-btn"
                  cursor="pointer"
                  onClick={() => setPlay(false)}
                  position="relative"
                  w="25px"
                  h="25px"
                >
                  <Image layout="fill" src={PauseIcon} alt="pause-icon" />
                </Box>
              ) : (
                <Box
                  id="play-learn-btn"
                  cursor="pointer"
                  onClick={() => setPlay(true)}
                  position="relative"
                  w="25px"
                  h="25px"
                >
                  <Image layout="fill" src={PlayIcon} alt="play-icon" />
                </Box>
              )}

              <Box
                cursor="pointer"
                onClick={handleFastForward}
                position="relative"
                w="25px"
                h="25px"
              >
                <Image layout="fill" src={FastForwardIcon} alt="forward-icon" />
              </Box>

              <Text color="white">{elapsedTime}</Text>

              <Flex h="25px" justify="space-between">
                <Box w="550px">
                  <Slider
                    minH="32px"
                    min={0}
                    max={100}
                    value={played * 100}
                    onChange={(val) => handleSeekChange(val)}
                    onMouseDown={handleSeekMouseDown}
                    onChangeEnd={(val) => handleSeekMouseUp(val)}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </Flex>

              <Text color="white">{totalDuration}</Text>

              <Flex
                onMouseEnter={() => setShowVolumeSlider(true)}
                onMouseLeave={() => setShowVolumeSlider(false)}
                cursor="pointer"
                h="25px"
                justify="space-between"
              >
                <Box position="relative" w="70px" h="25px">
                  <Image layout="fill" src={VolumeIcon} alt="volume-icon" />
                </Box>
                <Box w="90px" display={showVolumeSlider ? 'flex' : 'none'}>
                  <Slider
                    minH="32px"
                    defaultValue={20}
                    value={volume * 100}
                    onChange={(val) => handleVolumeChange(val)}
                    // isDisabled={isMuted ? true : false}
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>
              </Flex>

              <Menu>
                <MenuButton as={Box} cursor="pointer">
                  <Box position="relative" w="25px" h="25px">
                    <Image
                      layout="fill"
                      src={SettingsIcon}
                      alt="settings-icon"
                    />
                  </Box>
                </MenuButton>
                <MenuList>
                  <MenuOptionGroup defaultValue="1" title="Speed" type="radio">
                    <MenuItemOption
                      onClick={() => setPlaybackRate(1.0)}
                      value="1"
                    >
                      <Text>1x</Text>
                    </MenuItemOption>
                    <MenuItemOption
                      onClick={() => setPlaybackRate(1.5)}
                      value="1.5"
                    >
                      <Text>1.5x</Text>
                    </MenuItemOption>
                    <MenuItemOption
                      onClick={() => setPlaybackRate(2.0)}
                      value="2"
                    >
                      <Text>2x</Text>
                    </MenuItemOption>
                  </MenuOptionGroup>
                </MenuList>
              </Menu>

              <Box
                onClick={toggleFullScreen}
                cursor="pointer"
                position="relative"
                w="40px"
                h="25px"
              >
                <Image
                  layout="fill"
                  src={FullScreenIcon}
                  alt="full-screen-icon"
                />
              </Box>
            </Flex>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}

export default HeaderVideo
